import {
  ATLAS_PAGE_SIZE,
  GlyphAtlas,
  type AtlasGlyph,
  type AtlasPageStorage,
  type GlyphBitmap,
  type GlyphKey,
  type GlyphRasterRequest,
  type GlyphRasterizer,
} from './atlas'
import {
  CANVAS_BACKING_MAX_DIMENSION,
  CANVAS_BACKING_MAX_PIXELS,
  CANVAS_DIRECT_GLYPH_MAX_DIMENSION,
  CANVAS_DIRECT_GLYPH_MAX_PIXELS,
} from './canvas-renderer'
import type { GridRendererConfig, GridRgb } from './config'
import {
  EMPTY_IMAGE_DRAWS,
  validateImageDraws,
  type ImageDraw,
  type ImageGeometryRenderAsset,
} from './image-geometry'
import type {
  BrowserImageLease,
  BrowserImageRenderAsset,
  BrowserImageRenderDescription,
  ImageRepresentationStore,
} from './image-store'
import {
  visitGeometry,
  type GeometryBox,
  type GeometryContentCommand,
  type GeometryGlyph,
  type GeometryRect,
  type GridGeometry,
} from './geometry'
import {
  GLYPH_FRAGMENT_SHADER,
  GLYPH_VERTEX_SHADER,
  IMAGE_FRAGMENT_SHADER,
  IMAGE_VERTEX_SHADER,
  SOLID_FRAGMENT_SHADER,
  SOLID_VERTEX_SHADER,
  linkGridProgram,
} from './shaders'

export type WebGLRendererState = 'ready' | 'lost' | 'restoring' | 'fallback' | 'disposed'

export interface WebGLRenderFrame {
  readonly geometry: GridGeometry
  readonly images?: readonly ImageDraw[]
  readonly config: GridRendererConfig
  readonly cols: number
  readonly rows: number
  readonly dpr: number
  readonly viewportWidth?: number
  readonly viewportHeight?: number
}

export interface WebGLRendererOptions {
  readonly onFallbackRequested?: (error: unknown) => void
  readonly onRestored?: () => void
  readonly contextAttributes?: WebGLContextAttributes
  readonly imageStore?: ImageRepresentationStore & {
    describeForRender?(assetId: number): BrowserImageRenderDescription | null
    acquireForRender?(assetId: number): BrowserImageLease<BrowserImageRenderAsset> | null
  }
}

export interface WebGLGlyphAlphaUsage {
  readonly currentBytes: number
  readonly currentCount: number
  readonly peakBytes: number
  readonly peakCount: number
}

interface ConfigurableGlyphRasterizer extends GlyphRasterizer {
  configure(config: GridRendererConfig, dpr: number): void
}

interface FrameSize {
  readonly width: number
  readonly height: number
  readonly backingWidth: number
  readonly backingHeight: number
}

interface ContentSurface {
  readonly texture: WebGLTexture
  readonly framebuffer: WebGLFramebuffer
  readonly width: number
  readonly height: number
}

interface ProgramResources {
  readonly value: WebGLProgram
  readonly resolution: WebGLUniformLocation
  readonly page?: WebGLUniformLocation
}

interface GpuResources {
  readonly solid: ProgramResources
  readonly glyph: ProgramResources & { readonly page: WebGLUniformLocation }
  readonly image: ProgramResources & { readonly page: WebGLUniformLocation }
  readonly solidVao: WebGLVertexArrayObject
  readonly glyphVao: WebGLVertexArrayObject
  readonly imageVao: WebGLVertexArrayObject
  readonly unitBuffer: WebGLBuffer
  readonly solidInstanceBuffer: WebGLBuffer
  readonly glyphInstanceBuffer: WebGLBuffer
  readonly imageInstanceBuffer: WebGLBuffer
  readonly maxTextureSize: number
  readonly maxViewportWidth: number
  readonly maxViewportHeight: number
}

interface WebGLAtlasPage {
  readonly texture: WebGLTexture
}

interface FrameInstances {
  readonly beforeGlyphs: readonly number[]
  readonly glyphs: readonly GeometryGlyph[]
  readonly decorations: readonly number[]
  readonly overlays: readonly number[]
}

interface GlyphWork {
  readonly key: GlyphKey
  bitmapMeta: GlyphBitmapMeta | undefined
}

interface GlyphBitmapMeta {
  readonly width: number
  readonly height: number
  readonly offsetX: number
  readonly offsetY: number
}

interface StreamingGlyphPlacement extends GlyphBitmapMeta {
  readonly dpr: number
  readonly u0: number
  readonly v0: number
  readonly u1: number
  readonly v1: number
}

interface RenderTransaction {
  readonly epoch: number
  readonly gl: WebGL2RenderingContext
  readonly gpu: GpuResources
  atlas: GlyphAtlas<WebGLAtlasPage> | null
  candidate: ContentSurface | null
  streamingTexture: WebGLTexture | null
  streamingPage: Uint8Array | null
  readonly bitmapCache: Map<string, GlyphBitmap>
  bitmapCacheBytes: number
  canceled: boolean
}

interface ImageTextureEntry {
  readonly contentHash: string
  readonly owner: string
  readonly texture: WebGLTexture
  readonly bytes: number
}

interface AcquiredImageTexture {
  readonly entry: ImageTextureEntry
  readonly lease: BrowserImageLease<undefined>
}

const DEFAULT_CONTEXT_ATTRIBUTES: WebGLContextAttributes = Object.freeze({
  alpha: false,
  antialias: false,
  depth: false,
  stencil: false,
  premultipliedAlpha: false,
  preserveDrawingBuffer: false,
})

const UNIT_QUAD = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1])
const SOLID_INSTANCE_FLOATS = 8
const GLYPH_INSTANCE_FLOATS = 12
const IMAGE_INSTANCE_FLOATS = 8
const GRID_MAX_COLS = 500
const GRID_MAX_ROWS = 200
const GRID_MAX_CELLS = GRID_MAX_COLS * GRID_MAX_ROWS
const GRID_MAX_RETAINED_COMMANDS = 4 * GRID_MAX_CELLS + 2
// A procedural box can expand to four double-line arms (eight solid quads).
// These caps are checked from retained-array lengths before copying or visiting.
const GRID_MAX_SOLID_INSTANCES = 11 * GRID_MAX_CELLS + 2
const GRID_MAX_GLYPH_INSTANCES = GRID_MAX_CELLS
const GLYPH_ALPHA_MAX_BYTES = 2 * ATLAS_PAGE_SIZE * ATLAS_PAGE_SIZE
const GLYPH_ALPHA_MAX_COUNT = 2
const MAX_FONT_SOURCE_BYTES = 32 * 1024 * 1024 - 44
const FONT_VARIANTS = new Set(['regular', 'bold', 'italic', 'bold-italic'])
const CURSOR_STYLES = new Set(['block', 'bar', 'underline'])
let nextImageRendererId = 1

const EMPTY_GEOMETRY: GridGeometry = Object.freeze({
  backgrounds: Object.freeze([]), content: Object.freeze([]),
  decorations: Object.freeze([]), selection: Object.freeze([]),
  cursor: Object.freeze([]), retainedCommandCount: 0,
})

function bestEffort(action: () => void): void {
  try { action() } catch {}
}

function copyRgb(color: GridRgb): GridRgb {
  return Object.freeze([color[0], color[1], color[2]]) as GridRgb
}

function copyRect(command: GeometryRect): GeometryRect {
  return Object.freeze({ ...command, color: copyRgb(command.color) })
}

function copyGlyph(command: GeometryGlyph): GeometryGlyph {
  return Object.freeze({ ...command, color: copyRgb(command.color) })
}

function copyBox(command: GeometryBox): GeometryBox {
  return Object.freeze({
    ...command,
    spec: Object.freeze({ ...command.spec }),
    color: copyRgb(command.color),
  })
}

function copyContent(command: GeometryContentCommand): GeometryContentCommand {
  return command.kind === 'glyph' ? copyGlyph(command) : copyBox(command)
}

function copyGeometry(geometry: GridGeometry): GridGeometry {
  return Object.freeze({
    backgrounds: Object.freeze(geometry.backgrounds.map(copyRect)),
    content: Object.freeze(geometry.content.map(copyContent)),
    decorations: Object.freeze(geometry.decorations.map(copyRect)),
    selection: Object.freeze(geometry.selection.map(copyRect)),
    cursor: Object.freeze(geometry.cursor.map(copyRect)),
    retainedCommandCount: geometry.retainedCommandCount,
  })
}

function copyImageDraw(draw: ImageDraw): ImageDraw {
  return Object.freeze({ ...draw })
}

function replaceGeometryOverlays(base: GridGeometry, source: GridGeometry): GridGeometry {
  const selection = Object.freeze(source.selection.map(copyRect))
  const cursor = Object.freeze(source.cursor.map(copyRect))
  return Object.freeze({
    backgrounds: base.backgrounds,
    content: base.content,
    decorations: base.decorations,
    selection,
    cursor,
    retainedCommandCount: base.backgrounds.length + base.content.length +
      base.decorations.length + selection.length + cursor.length,
  })
}

function copyConfig(config: GridRendererConfig): GridRendererConfig {
  return Object.freeze({
    ...config,
    palette: Object.freeze(config.palette.map(copyRgb)),
    foreground: copyRgb(config.foreground),
    background: copyRgb(config.background),
    cursor: copyRgb(config.cursor),
    fonts: Object.freeze(config.fonts.map((font) => Object.freeze({ ...font }))),
  })
}

function retainFrame(frame: WebGLRenderFrame): WebGLRenderFrame {
  return Object.freeze({
    geometry: copyGeometry(frame.geometry),
    images: Object.freeze((frame.images ?? EMPTY_IMAGE_DRAWS).map(copyImageDraw)),
    config: copyConfig(frame.config),
    cols: frame.cols,
    rows: frame.rows,
    dpr: frame.dpr,
    ...(frame.viewportWidth === undefined ? {} : { viewportWidth: frame.viewportWidth }),
    ...(frame.viewportHeight === undefined ? {} : { viewportHeight: frame.viewportHeight }),
  })
}

function boundedInteger(value: number, minimum: number, maximum: number): boolean {
  return Number.isInteger(value) && value >= minimum && value <= maximum
}

function validateConfig(config: GridRendererConfig): void {
  if (!boundedInteger(config.configGeneration, 1, 0xffffffff) ||
      !boundedInteger(config.profileFontSize, 0, 0xffff) ||
      !boundedInteger(config.fontSize, 6, 96) ||
      !Number.isFinite(config.lineHeight) || config.lineHeight <= 0 ||
      !Number.isFinite(config.cssPxSize) || config.cssPxSize <= 0 ||
      !boundedInteger(config.cellWidth, 1, 0xffff) ||
      !boundedInteger(config.cellHeight, 1, 0xffff) ||
      !boundedInteger(config.baseline, -0x8000, 0x7fff) ||
      !CURSOR_STYLES.has(config.cursorStyle) || typeof config.cursorBlink !== 'boolean' ||
      !Array.isArray(config.palette) || config.palette.length !== 256 ||
      !Array.isArray(config.fonts) || config.fonts.length < 1 || config.fonts.length > 4) {
    throw new RangeError('WebGL renderer configuration is invalid')
  }
  for (const color of config.palette) validateColor(color, 255)
  validateColor(config.foreground, 255)
  validateColor(config.background, 255)
  validateColor(config.cursor, 255)
  const variants = new Set<string>()
  for (const font of config.fonts) {
    if (!FONT_VARIANTS.has(font.variant) || variants.has(font.variant) ||
        !/^[0-9a-f]{64}$/u.test(font.hash) ||
        !boundedInteger(font.byteLength, 1, MAX_FONT_SOURCE_BYTES) ||
        typeof font.embedded !== 'boolean' || (font.embedded && font.variant !== 'regular')) {
      throw new RangeError('WebGL renderer font configuration is invalid')
    }
    variants.add(font.variant)
  }
  if (!variants.has('regular')) throw new RangeError('WebGL renderer font configuration is invalid')
}

function geometryArrays(geometry: GridGeometry): readonly (readonly unknown[])[] {
  const arrays = [geometry.backgrounds, geometry.content, geometry.decorations,
    geometry.selection, geometry.cursor]
  if (!arrays.every(Array.isArray)) throw new RangeError('WebGL geometry arrays are invalid')
  return arrays
}

function preflightGeometry(geometry: GridGeometry, cells: number): void {
  const arrays = geometryArrays(geometry)
  const lengths = arrays.map((values) => values.length)
  const total = lengths.reduce((sum, length) => sum + length, 0)
  const expandedSolidUpperBound = lengths[0] + 8 * lengths[1] +
    lengths[2] + lengths[3] + lengths[4]
  if (lengths[0] > cells || lengths[1] > cells || lengths[2] > 2 * cells ||
      lengths[3] > 1 || lengths[4] > 1 || total > 4 * cells + 2 ||
      total > GRID_MAX_RETAINED_COMMANDS || lengths[1] > GRID_MAX_GLYPH_INSTANCES ||
      expandedSolidUpperBound > GRID_MAX_SOLID_INSTANCES ||
      geometry.retainedCommandCount !== total) {
    throw new RangeError('WebGL geometry command bound is invalid')
  }
}

function validateRectCommand(command: GeometryRect, layer: GeometryRect['layer'],
                             primitive: GeometryRect['primitive']): void {
  if (command === null || typeof command !== 'object' || command.kind !== 'rect' ||
      command.layer !== layer || command.primitive !== primitive ||
      ![command.x, command.y, command.width, command.height].every(Number.isFinite) ||
      command.width < 0 || command.height < 0) {
    throw new RangeError('WebGL geometry rectangle is invalid')
  }
  validateColor(command.color, command.alpha)
}

function validateBox(command: GeometryBox): void {
  if (command === null || typeof command !== 'object' || command.kind !== 'box' ||
      command.layer !== 'content' ||
      ![command.x, command.y, command.width, command.height].every(Number.isFinite) ||
      command.width < 0 || command.height < 0 || command.spec === null ||
      typeof command.spec !== 'object') {
    throw new RangeError('WebGL geometry box is invalid')
  }
  const { up, down, left, right, shade, fillEighths, fillEdge } = command.spec
  if (![up, down, left, right].every((value) => boundedInteger(value, 0, 3)) ||
      ![0, 64, 128, 192].includes(shade) || !boundedInteger(fillEighths, 0, 8) ||
      !boundedInteger(fillEdge, 0, 4) ||
      (shade !== 0 && (up + down + left + right + fillEighths + fillEdge !== 0)) ||
      (fillEighths !== 0 && (up + down + left + right + shade !== 0 || fillEdge === 0)) ||
      (fillEighths === 0 && fillEdge !== 0)) {
    throw new RangeError('WebGL geometry box specification is invalid')
  }
  validateColor(command.color, command.alpha)
}

function validateGeometry(geometry: GridGeometry, cells: number): void {
  preflightGeometry(geometry, cells)
  for (const command of geometry.backgrounds) validateRectCommand(command, 'background', 'background')
  for (const command of geometry.content) {
    if (command.kind === 'glyph') {
      if (command.layer !== 'content') throw new RangeError('WebGL geometry glyph layer is invalid')
      validateGlyph(command)
    } else if (command.kind === 'box') {
      validateBox(command)
    } else {
      throw new RangeError('WebGL geometry content command is invalid')
    }
  }
  for (const command of geometry.decorations) validateRectCommand(command, 'decoration', 'decoration')
  for (const command of geometry.selection) validateRectCommand(command, 'selection', 'selection')
  for (const command of geometry.cursor) validateRectCommand(command, 'cursor', 'cursor')
}

function validateFrame(frame: WebGLRenderFrame): FrameSize {
  if (frame === null || typeof frame !== 'object' ||
      !boundedInteger(frame.cols, 0, GRID_MAX_COLS) ||
      !boundedInteger(frame.rows, 0, GRID_MAX_ROWS) ||
      frame.cols * frame.rows > GRID_MAX_CELLS) {
    throw new RangeError('WebGL renderer frame dimensions are invalid')
  }
  validateConfig(frame.config)
  const size = frameSize(frame)
  validateGeometry(frame.geometry, frame.cols * frame.rows)
  validateImageDraws(frame.images ?? EMPTY_IMAGE_DRAWS)
  return size
}

function retainOverlayGeometry(base: GridGeometry, source: GridGeometry): GridGeometry {
  const arrays = geometryArrays(source)
  if (arrays[3].length > 1 || arrays[4].length > 1) {
    throw new RangeError('WebGL overlay command bound is invalid')
  }
  for (const command of source.selection) validateRectCommand(command, 'selection', 'selection')
  for (const command of source.cursor) validateRectCommand(command, 'cursor', 'cursor')
  return replaceGeometryOverlays(base, source)
}

class WebGLGpuError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'WebGLGpuError'
  }
}

class WebGLContextLostError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'WebGLContextLostError'
  }
}

function required<T>(value: T | null, label: string): T {
  if (value === null) throw new WebGLGpuError(`WebGL ${label} allocation failed`)
  return value
}

function assertGlOkay(gl: WebGL2RenderingContext, label: string): void {
  const lost = gl.isContextLost()
  const error = gl.getError()
  if (lost || error === gl.CONTEXT_LOST_WEBGL) {
    throw new WebGLContextLostError(`WebGL context was lost during ${label}`)
  }
  if (error === gl.NO_ERROR) return
  throw new WebGLGpuError(`WebGL ${label} failed with error 0x${error.toString(16)}`)
}

function reportsContextLost(gl: WebGL2RenderingContext): boolean {
  try {
    return typeof gl.isContextLost === 'function' && gl.isContextLost()
  } catch {
    return false
  }
}

function classifyGlFailure(gl: WebGL2RenderingContext, error: unknown,
                           label: string): unknown {
  return error instanceof WebGLContextLostError || !reportsContextLost(gl)
    ? error
    : new WebGLContextLostError(`WebGL context was lost during ${label}`)
}

function isConfigurable(value: GlyphRasterizer): value is ConfigurableGlyphRasterizer {
  return 'configure' in value && typeof (value as Partial<ConfigurableGlyphRasterizer>).configure === 'function'
}

function frameSize(frame: WebGLRenderFrame): FrameSize {
  const { config } = frame
  if (!Number.isInteger(frame.cols) || frame.cols < 0 || frame.cols > 500 ||
      !Number.isInteger(frame.rows) || frame.rows < 0 || frame.rows > 200 ||
      !Number.isInteger(config.cellWidth) || config.cellWidth <= 0 ||
      !Number.isInteger(config.cellHeight) || config.cellHeight <= 0 ||
      !Number.isFinite(frame.dpr) || frame.dpr <= 0 || frame.dpr > 16) {
    throw new RangeError('WebGL renderer dimensions or DPR are invalid')
  }
  const gridWidth = frame.cols * config.cellWidth
  const gridHeight = frame.rows * config.cellHeight
  if (!Number.isSafeInteger(gridWidth) || !Number.isSafeInteger(gridHeight)) {
    throw new RangeError('WebGL renderer logical size is invalid')
  }
  const ownsViewport = frame.viewportWidth !== undefined || frame.viewportHeight !== undefined
  if (ownsViewport && (frame.viewportWidth === undefined || frame.viewportHeight === undefined ||
      !Number.isFinite(frame.viewportWidth) || frame.viewportWidth < 0 ||
      !Number.isFinite(frame.viewportHeight) || frame.viewportHeight < 0)) {
    throw new RangeError('WebGL renderer viewport is invalid')
  }
  const width = frame.viewportWidth ?? gridWidth
  const height = frame.viewportHeight ?? gridHeight
  const cssWidth = Math.ceil(width)
  const cssHeight = Math.ceil(height)
  const cssPixels = cssWidth * cssHeight
  if (ownsViewport && (!Number.isSafeInteger(cssWidth) || !Number.isSafeInteger(cssHeight) ||
      cssWidth > CANVAS_BACKING_MAX_DIMENSION || cssHeight > CANVAS_BACKING_MAX_DIMENSION ||
      !Number.isSafeInteger(cssPixels) || cssPixels > CANVAS_BACKING_MAX_PIXELS)) {
    throw new RangeError('WebGL viewport backing exceeds supported bounds')
  }
  const backingWidth = Math.round(width * frame.dpr)
  const backingHeight = Math.round(height * frame.dpr)
  const pixels = backingWidth * backingHeight
  if (!Number.isSafeInteger(backingWidth) || !Number.isSafeInteger(backingHeight) ||
      (width > 0 && backingWidth === 0) || (height > 0 && backingHeight === 0) ||
      backingWidth > CANVAS_BACKING_MAX_DIMENSION ||
      backingHeight > CANVAS_BACKING_MAX_DIMENSION ||
      !Number.isSafeInteger(pixels) || pixels > CANVAS_BACKING_MAX_PIXELS) {
    throw new RangeError('WebGL backing size exceeds supported bounds')
  }
  return Object.freeze({ width, height, backingWidth, backingHeight })
}

function validByte(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 255
}

function validateColor(color: GridRgb, alpha: number): void {
  if (!Array.isArray(color) || color.length !== 3 || !color.every(validByte) || !validByte(alpha)) {
    throw new RangeError('WebGL geometry contains an invalid resolved color')
  }
}

function appendRect(target: number[], x: number, y: number, width: number, height: number,
                    color: GridRgb, alpha: number): void {
  if (![x, y, width, height].every(Number.isFinite) || width < 0 || height < 0) {
    throw new RangeError('WebGL geometry contains an invalid rectangle')
  }
  validateColor(color, alpha)
  if (width === 0 || height === 0 || alpha === 0) return
  target.push(x, y, width, height,
    color[0] / 255, color[1] / 255, color[2] / 255, alpha / 255)
}

function validateGlyph(command: GeometryGlyph): void {
  if (!Number.isInteger(command.codepoint) || command.codepoint < 0 || command.codepoint > 0x10ffff ||
      (command.codepoint >= 0xd800 && command.codepoint <= 0xdfff) ||
      !Number.isInteger(command.variant) || command.variant < 0 || command.variant > 3 ||
      !Number.isFinite(command.x) || !Number.isFinite(command.y) ||
      typeof command.embolden !== 'boolean' || typeof command.shear !== 'boolean') {
    throw new RangeError('WebGL geometry contains an invalid glyph')
  }
  validateColor(command.color, command.alpha)
}

function collectInstances(geometry: GridGeometry): FrameInstances {
  const beforeGlyphs: number[] = []
  const glyphs: GeometryGlyph[] = []
  const decorations: number[] = []
  const overlays: number[] = []
  visitGeometry(geometry, {
    rect: (layer, _primitive, x, y, width, height, color, alpha) => {
      if (layer === 'background' || layer === 'content') {
        appendRect(beforeGlyphs, x, y, width, height, color, alpha)
      } else if (layer === 'decoration') {
        appendRect(decorations, x, y, width, height, color, alpha)
      } else {
        appendRect(overlays, x, y, width, height, color, alpha)
      }
    },
    glyph: (command) => {
      validateGlyph(command)
      glyphs.push(command)
    },
  })
  return { beforeGlyphs, glyphs, decorations, overlays }
}

function collectOverlays(geometry: GridGeometry): readonly number[] {
  const overlays: number[] = []
  for (const command of geometry.selection) {
    appendRect(overlays, command.x, command.y, command.width, command.height,
      command.color, command.alpha)
  }
  for (const command of geometry.cursor) {
    appendRect(overlays, command.x, command.y, command.width, command.height,
      command.color, command.alpha)
  }
  return overlays
}

function glyphKey(command: GeometryGlyph): GlyphKey {
  return {
    codepoint: command.codepoint,
    variant: command.variant,
    embolden: command.embolden,
    shear: command.shear,
  }
}

function glyphKeyId(key: GlyphKey): string {
  return `${key.codepoint}/${key.variant}/${key.embolden ? 1 : 0}/${key.shear ? 1 : 0}`
}

function groupGlyphWork(commands: readonly GeometryGlyph[]): GlyphWork[] {
  const grouped = new Map<string, GlyphWork>()
  for (const command of commands) {
    const key = glyphKey(command)
    const id = glyphKeyId(key)
    if (!grouped.has(id)) grouped.set(id, { key, bitmapMeta: undefined })
  }
  return [...grouped.values()]
}

function validateBitmap(bitmap: GlyphBitmap): void {
  const pixels = bitmap.width * bitmap.height
  if (!Number.isInteger(bitmap.width) || bitmap.width < 0 ||
      !Number.isInteger(bitmap.height) || bitmap.height < 0 ||
      bitmap.width > CANVAS_DIRECT_GLYPH_MAX_DIMENSION ||
      bitmap.height > CANVAS_DIRECT_GLYPH_MAX_DIMENSION ||
      !Number.isSafeInteger(pixels) || pixels > CANVAS_DIRECT_GLYPH_MAX_PIXELS ||
      !Number.isFinite(bitmap.offsetX) || !Number.isFinite(bitmap.offsetY) ||
      !(bitmap.alpha instanceof Uint8Array) || bitmap.alpha.byteLength !== pixels) {
    throw new RangeError('WebGL direct glyph exceeds bounded raster surface')
  }
}

function glyphBitmapMeta(bitmap: GlyphBitmap): GlyphBitmapMeta {
  return Object.freeze({
    width: bitmap.width, height: bitmap.height,
    offsetX: bitmap.offsetX, offsetY: bitmap.offsetY,
  })
}

function bitmapMatchesMeta(bitmap: GlyphBitmap, meta: GlyphBitmapMeta): boolean {
  return bitmap.width === meta.width && bitmap.height === meta.height &&
    Object.is(bitmap.offsetX, meta.offsetX) && Object.is(bitmap.offsetY, meta.offsetY)
}

function setCanvasSizeExact(canvas: HTMLCanvasElement, width: number, height: number): void {
  if (canvas.width !== 0) canvas.width = 0
  if (canvas.height !== 0) canvas.height = 0
  canvas.width = width
  canvas.height = height
  if (canvas.width !== width || canvas.height !== height) {
    throw new RangeError('WebGL canvas rejected validated backing dimensions')
  }
}

class WebGLAtlasStorage implements AtlasPageStorage<WebGLAtlasPage> {
  readonly #gl: WebGL2RenderingContext

  constructor(gl: WebGL2RenderingContext) {
    this.#gl = gl
  }

  createPage(_index: number, size: number): WebGLAtlasPage {
    const texture = required(this.#gl.createTexture(), 'atlas texture')
    try {
      this.#configure(texture)
      this.#gl.texImage2D(this.#gl.TEXTURE_2D, 0, this.#gl.R8,
        size, size, 0, this.#gl.RED, this.#gl.UNSIGNED_BYTE, null)
      assertGlOkay(this.#gl, 'atlas page allocation')
      return { texture }
    } catch (error) {
      this.#gl.deleteTexture(texture)
      throw error
    }
  }

  clearPage(page: WebGLAtlasPage): void {
    this.#configure(page.texture)
    this.#gl.texImage2D(this.#gl.TEXTURE_2D, 0, this.#gl.R8,
      ATLAS_PAGE_SIZE, ATLAS_PAGE_SIZE, 0, this.#gl.RED, this.#gl.UNSIGNED_BYTE, null)
    assertGlOkay(this.#gl, 'atlas page clear')
  }

  destroyPage(page: WebGLAtlasPage): void {
    this.#gl.deleteTexture(page.texture)
  }

  upload(page: WebGLAtlasPage, x: number, y: number, bitmap: GlyphBitmap): void {
    this.#configure(page.texture)
    this.#gl.pixelStorei(this.#gl.UNPACK_ALIGNMENT, 1)
    this.#gl.texSubImage2D(this.#gl.TEXTURE_2D, 0, x, y,
      bitmap.width, bitmap.height, this.#gl.RED, this.#gl.UNSIGNED_BYTE, bitmap.alpha)
    assertGlOkay(this.#gl, 'atlas glyph upload')
  }

  #configure(texture: WebGLTexture): void {
    const gl = this.#gl
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  }
}

function configureSolidVao(gl: WebGL2RenderingContext, vao: WebGLVertexArrayObject,
                           unitBuffer: WebGLBuffer, instanceBuffer: WebGLBuffer): void {
  gl.bindVertexArray(vao)
  gl.bindBuffer(gl.ARRAY_BUFFER, unitBuffer)
  gl.enableVertexAttribArray(0)
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
  gl.vertexAttribDivisor(0, 0)
  gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer)
  gl.enableVertexAttribArray(1)
  gl.vertexAttribPointer(1, 4, gl.FLOAT, false, SOLID_INSTANCE_FLOATS * 4, 0)
  gl.vertexAttribDivisor(1, 1)
  gl.enableVertexAttribArray(2)
  gl.vertexAttribPointer(2, 4, gl.FLOAT, false, SOLID_INSTANCE_FLOATS * 4, 4 * 4)
  gl.vertexAttribDivisor(2, 1)
}

function configureGlyphVao(gl: WebGL2RenderingContext, vao: WebGLVertexArrayObject,
                           unitBuffer: WebGLBuffer, instanceBuffer: WebGLBuffer): void {
  gl.bindVertexArray(vao)
  gl.bindBuffer(gl.ARRAY_BUFFER, unitBuffer)
  gl.enableVertexAttribArray(0)
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
  gl.vertexAttribDivisor(0, 0)
  gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer)
  gl.enableVertexAttribArray(1)
  gl.vertexAttribPointer(1, 4, gl.FLOAT, false, GLYPH_INSTANCE_FLOATS * 4, 0)
  gl.vertexAttribDivisor(1, 1)
  gl.enableVertexAttribArray(2)
  gl.vertexAttribPointer(2, 4, gl.FLOAT, false, GLYPH_INSTANCE_FLOATS * 4, 4 * 4)
  gl.vertexAttribDivisor(2, 1)
  gl.enableVertexAttribArray(3)
  gl.vertexAttribPointer(3, 4, gl.FLOAT, false, GLYPH_INSTANCE_FLOATS * 4, 8 * 4)
  gl.vertexAttribDivisor(3, 1)
}

function configureImageVao(gl: WebGL2RenderingContext, vao: WebGLVertexArrayObject,
                           unitBuffer: WebGLBuffer, instanceBuffer: WebGLBuffer): void {
  gl.bindVertexArray(vao)
  gl.bindBuffer(gl.ARRAY_BUFFER, unitBuffer)
  gl.enableVertexAttribArray(0)
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
  gl.vertexAttribDivisor(0, 0)
  gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer)
  gl.enableVertexAttribArray(1)
  gl.vertexAttribPointer(1, 4, gl.FLOAT, false, IMAGE_INSTANCE_FLOATS * 4, 0)
  gl.vertexAttribDivisor(1, 1)
  gl.enableVertexAttribArray(3)
  gl.vertexAttribPointer(3, 4, gl.FLOAT, false, IMAGE_INSTANCE_FLOATS * 4, 4 * 4)
  gl.vertexAttribDivisor(3, 1)
}

function createGpuResources(gl: WebGL2RenderingContext): GpuResources {
  let solidProgram: WebGLProgram | null = null
  let glyphProgram: WebGLProgram | null = null
  let imageProgram: WebGLProgram | null = null
  let solidVao: WebGLVertexArrayObject | null = null
  let glyphVao: WebGLVertexArrayObject | null = null
  let imageVao: WebGLVertexArrayObject | null = null
  let unitBuffer: WebGLBuffer | null = null
  let solidInstanceBuffer: WebGLBuffer | null = null
  let glyphInstanceBuffer: WebGLBuffer | null = null
  let imageInstanceBuffer: WebGLBuffer | null = null
  try {
    for (const name of ['createVertexArray', 'bindVertexArray', 'vertexAttribDivisor',
      'drawArraysInstanced', 'blitFramebuffer', 'getError', 'isContextLost'] as const) {
      if (typeof gl[name] !== 'function') throw new WebGLGpuError(`WebGL2 capability ${name} is unavailable`)
    }
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as unknown
    const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as unknown
    if (!Number.isInteger(maxTextureSize) || (maxTextureSize as number) < ATLAS_PAGE_SIZE ||
        !(maxViewport instanceof Int32Array) || maxViewport.length < 2 ||
        maxViewport[0] < ATLAS_PAGE_SIZE || maxViewport[1] < ATLAS_PAGE_SIZE) {
      throw new WebGLGpuError('WebGL2 texture or viewport capability is insufficient')
    }
    assertGlOkay(gl, 'capability query')
    solidProgram = linkGridProgram(gl, SOLID_VERTEX_SHADER, SOLID_FRAGMENT_SHADER)
    glyphProgram = linkGridProgram(gl, GLYPH_VERTEX_SHADER, GLYPH_FRAGMENT_SHADER)
    imageProgram = linkGridProgram(gl, IMAGE_VERTEX_SHADER, IMAGE_FRAGMENT_SHADER)
    solidVao = required(gl.createVertexArray(), 'solid vertex array')
    glyphVao = required(gl.createVertexArray(), 'glyph vertex array')
    imageVao = required(gl.createVertexArray(), 'image vertex array')
    unitBuffer = required(gl.createBuffer(), 'unit quad buffer')
    solidInstanceBuffer = required(gl.createBuffer(), 'solid instance buffer')
    glyphInstanceBuffer = required(gl.createBuffer(), 'glyph instance buffer')
    imageInstanceBuffer = required(gl.createBuffer(), 'image instance buffer')

    gl.bindBuffer(gl.ARRAY_BUFFER, unitBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, UNIT_QUAD, gl.STATIC_DRAW)
    configureSolidVao(gl, solidVao, unitBuffer, solidInstanceBuffer)
    configureGlyphVao(gl, glyphVao, unitBuffer, glyphInstanceBuffer)
    configureImageVao(gl, imageVao, unitBuffer, imageInstanceBuffer)
    assertGlOkay(gl, 'buffer and vertex-array initialization')
    gl.bindVertexArray(null)
    gl.bindBuffer(gl.ARRAY_BUFFER, null)

    const solidResolution = required(gl.getUniformLocation(solidProgram, 'u_resolution'), 'solid resolution uniform')
    const glyphResolution = required(gl.getUniformLocation(glyphProgram, 'u_resolution'), 'glyph resolution uniform')
    const glyphPage = required(gl.getUniformLocation(glyphProgram, 'u_page'), 'glyph page uniform')
    const imageResolution = required(gl.getUniformLocation(imageProgram, 'u_resolution'), 'image resolution uniform')
    const imagePage = required(gl.getUniformLocation(imageProgram, 'u_image'), 'image texture uniform')
    return {
      solid: { value: solidProgram, resolution: solidResolution },
      glyph: { value: glyphProgram, resolution: glyphResolution, page: glyphPage },
      image: { value: imageProgram, resolution: imageResolution, page: imagePage },
      solidVao, glyphVao, imageVao, unitBuffer,
      solidInstanceBuffer, glyphInstanceBuffer, imageInstanceBuffer,
      maxTextureSize: maxTextureSize as number,
      maxViewportWidth: maxViewport[0],
      maxViewportHeight: maxViewport[1],
    }
  } catch (error) {
    if (imageInstanceBuffer !== null) bestEffort(() => { gl.deleteBuffer(imageInstanceBuffer!) })
    if (glyphInstanceBuffer !== null) bestEffort(() => { gl.deleteBuffer(glyphInstanceBuffer!) })
    if (solidInstanceBuffer !== null) bestEffort(() => { gl.deleteBuffer(solidInstanceBuffer!) })
    if (unitBuffer !== null) bestEffort(() => { gl.deleteBuffer(unitBuffer!) })
    if (glyphVao !== null) bestEffort(() => { gl.deleteVertexArray(glyphVao!) })
    if (imageVao !== null) bestEffort(() => { gl.deleteVertexArray(imageVao!) })
    if (solidVao !== null) bestEffort(() => { gl.deleteVertexArray(solidVao!) })
    if (glyphProgram !== null) bestEffort(() => { gl.deleteProgram(glyphProgram!) })
    if (imageProgram !== null) bestEffort(() => { gl.deleteProgram(imageProgram!) })
    if (solidProgram !== null) bestEffort(() => { gl.deleteProgram(solidProgram!) })
    throw classifyGlFailure(gl, error, 'resource initialization')
  }
}

function deleteGpuResources(gl: WebGL2RenderingContext, resources: GpuResources): void {
  bestEffort(() => { gl.deleteBuffer(resources.imageInstanceBuffer) })
  bestEffort(() => { gl.deleteBuffer(resources.glyphInstanceBuffer) })
  bestEffort(() => { gl.deleteBuffer(resources.solidInstanceBuffer) })
  bestEffort(() => { gl.deleteBuffer(resources.unitBuffer) })
  bestEffort(() => { gl.deleteVertexArray(resources.glyphVao) })
  bestEffort(() => { gl.deleteVertexArray(resources.imageVao) })
  bestEffort(() => { gl.deleteVertexArray(resources.solidVao) })
  bestEffort(() => { gl.deleteProgram(resources.glyph.value) })
  bestEffort(() => { gl.deleteProgram(resources.image.value) })
  bestEffort(() => { gl.deleteProgram(resources.solid.value) })
}

function createContentSurface(gl: WebGL2RenderingContext, width: number,
                              height: number): ContentSurface {
  const texture = required(gl.createTexture(), 'content texture')
  let framebuffer: WebGLFramebuffer | null = null
  try {
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8,
      Math.max(1, width), Math.max(1, height), 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    assertGlOkay(gl, 'content texture allocation')
    framebuffer = required(gl.createFramebuffer(), 'content framebuffer')
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D, texture, 0)
    assertGlOkay(gl, 'content framebuffer attachment')
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new WebGLGpuError('WebGL content framebuffer is incomplete')
    }
    return { texture, framebuffer, width, height }
  } catch (error) {
    if (framebuffer !== null) bestEffort(() => { gl.deleteFramebuffer(framebuffer!) })
    bestEffort(() => { gl.deleteTexture(texture) })
    throw error
  }
}

function deleteContentSurface(gl: WebGL2RenderingContext, surface: ContentSurface): void {
  bestEffort(() => { gl.deleteFramebuffer(surface.framebuffer) })
  bestEffort(() => { gl.deleteTexture(surface.texture) })
}

export class WebGLRenderer {
  readonly #canvas: HTMLCanvasElement
  readonly #rasterizer: GlyphRasterizer
  readonly #options: WebGLRendererOptions
  readonly #imageRendererId = nextImageRendererId++
  #gl: WebGL2RenderingContext | null = null
  #gpu: GpuResources | null = null
  #atlas: GlyphAtlas<WebGLAtlasPage> | null = null
  #surface: ContentSurface | null = null
  readonly #imageTextures = new Map<string, ImageTextureEntry>()
  #imageContextGeneration = 0
  #imageOwner = ''
  #latestFrame: WebGLRenderFrame | null = null
  #presentedFrame: WebGLRenderFrame | null = null
  #overlayGeometry: GridGeometry | null = null
  #activeRender: Promise<boolean> | null = null
  #transaction: RenderTransaction | null = null
  #fallbackNotificationQueued = false
  #fallbackNotified = false
  #state: WebGLRendererState = 'restoring'
  #renderEpoch = 0
  #logicalWidth = 0
  #logicalHeight = 0
  #dpr = 0
  #glyphAlphaCurrentBytes = 0
  #glyphAlphaCurrentCount = 0
  #glyphAlphaPeakBytes = 0
  #glyphAlphaPeakCount = 0

  readonly #handleContextLost = (event: Event): void => {
    event.preventDefault()
    if (this.#state === 'disposed' || this.#state === 'fallback') return
    ++this.#renderEpoch
    this.#disposeLogicalGpu()
    this.#state = 'lost'
  }

  readonly #handleContextRestored = (): void => {
    if (this.#state !== 'lost') return
    void this.#restore()
  }

  constructor(canvas: HTMLCanvasElement, rasterizer: GlyphRasterizer,
              options: WebGLRendererOptions = {}) {
    this.#canvas = canvas
    this.#rasterizer = rasterizer
    this.#options = options
    canvas.addEventListener('webglcontextlost', this.#handleContextLost)
    canvas.addEventListener('webglcontextrestored', this.#handleContextRestored)
    try {
      if (!this.#initialize()) {
        throw new WebGLGpuError('WebGL2 renderer is unavailable')
      }
    } catch (error) {
      this.#requestFallback(error)
    }
  }

  get state(): WebGLRendererState { return this.#state }

  get logicalSize(): Readonly<{ width: number; height: number; dpr: number }> {
    return Object.freeze({ width: this.#logicalWidth, height: this.#logicalHeight, dpr: this.#dpr })
  }

  // Logical renderer-owned alpha references, excluding browser/driver copies.
  // Peaks remain available after a frame or disposal for operational diagnostics.
  get glyphAlphaUsage(): WebGLGlyphAlphaUsage {
    return Object.freeze({
      currentBytes: this.#glyphAlphaCurrentBytes,
      currentCount: this.#glyphAlphaCurrentCount,
      peakBytes: this.#glyphAlphaPeakBytes,
      peakCount: this.#glyphAlphaPeakCount,
    })
  }

  acquireCachedImage(assetId: number): BrowserImageLease<ImageGeometryRenderAsset> | null {
    const store = this.#options.imageStore
    if (this.#state !== 'ready' || this.#gl === null || this.#gpu === null ||
        store?.describeForRender === undefined) return null
    const description = store.describeForRender(assetId)
    if (description === null) return null
    const entry = this.#imageTextures.get(description.contentHash)
    const bytes = description.width * description.height * 4
    if (entry === undefined || entry.owner !== this.#imageOwner ||
        entry.bytes !== bytes || !Number.isSafeInteger(bytes) || bytes <= 0) return null
    const lease = store.acquireRepresentation(assetId, entry.owner, entry.bytes, () => {})
    if (lease === null) return null
    const value: ImageGeometryRenderAsset = Object.freeze({
      ...description,
      rendererRepresentation: entry,
    })
    return Object.freeze({ value, release: () => lease.release() })
  }

  render(frame: WebGLRenderFrame): Promise<boolean> {
    if (this.#state === 'disposed') return Promise.reject(new Error('WebGL renderer is disposed'))
    if (this.#state === 'fallback') return Promise.resolve(false)
    let size: FrameSize
    let ownedFrame: WebGLRenderFrame
    let instances: FrameInstances
    try {
      size = validateFrame(frame)
      // Copy once, before any await or GPU mutation. Restoration therefore
      // owns a complete semantic frame and cannot observe caller changes.
      ownedFrame = retainFrame(frame)
      instances = collectInstances(ownedFrame.geometry)
    } catch (error) {
      return Promise.reject(error)
    }
    const operation = this.#renderValidated(ownedFrame, size, instances)
    this.#activeRender = operation
    void operation.then(
      () => { if (this.#activeRender === operation) this.#activeRender = null },
      () => { if (this.#activeRender === operation) this.#activeRender = null },
    )
    return operation
  }

  async #renderValidated(ownedFrame: WebGLRenderFrame, size: FrameSize,
                         instances: FrameInstances): Promise<boolean> {
    if (this.#state === 'disposed') throw new Error('WebGL renderer is disposed')
    if (this.#state === 'fallback') return false
    // Acceptance of a complete semantic frame is independent of whether a
    // GPU commit is currently possible. Visible size/state changes only on
    // the later surface commit.
    this.#latestFrame = ownedFrame
    this.#overlayGeometry = ownedFrame.geometry
    if (this.#state === 'lost' || this.#state === 'restoring') {
      // The model remains authoritative while no GPU is paintable. Keep the
      // latest complete semantic input for restoration; false means retained
      // successfully but not presented yet.
      return false
    }
    if (this.#state !== 'ready') return false
    const gl = this.#gl
    const gpu = this.#gpu
    if (gl === null || gpu === null) return false
    this.#cancelTransaction()
    let currentAtlas = this.#atlas
    const epoch = ++this.#renderEpoch
    isConfigurable(this.#rasterizer) && this.#rasterizer.configure(ownedFrame.config, ownedFrame.dpr)

    if (currentAtlas !== null &&
        (currentAtlas.configGeneration !== ownedFrame.config.configGeneration ||
         currentAtlas.dpr !== ownedFrame.dpr)) {
      this.#atlas = null
      bestEffort(() => { currentAtlas?.dispose() })
      currentAtlas = null
    }
    const atlas = currentAtlas ?? new GlyphAtlas(
      ownedFrame.config.configGeneration, ownedFrame.dpr,
      this.#rasterizer, new WebGLAtlasStorage(gl))
    const transaction: RenderTransaction = {
      epoch, gl, gpu, atlas, candidate: null, streamingTexture: null,
      streamingPage: null, bitmapCache: new Map(), bitmapCacheBytes: 0,
      canceled: false,
    }
    this.#transaction = transaction
    const glyphWork = groupGlyphWork(instances.glyphs)
    try {
      if (size.backingWidth > gpu.maxTextureSize || size.backingHeight > gpu.maxTextureSize ||
          size.backingWidth > gpu.maxViewportWidth || size.backingHeight > gpu.maxViewportHeight) {
        throw new WebGLGpuError('WebGL frame exceeds the available texture or viewport capability')
      }
      for (const work of glyphWork) {
        const result = await atlas.get(work.key)
        if (!this.#renderIsCurrent(transaction)) return false
        if (result === null) {
          const request = Object.freeze({
            ...work.key, configGeneration: ownedFrame.config.configGeneration,
            dpr: ownedFrame.dpr,
          })
          const bitmap = await this.#rasterizer.rasterize(request)
          if (!this.#renderIsCurrent(transaction)) return false
          work.bitmapMeta = this.#inspectGlyphBitmap(bitmap)
        } else if (result.kind === 'uncached') {
          work.bitmapMeta = this.#inspectGlyphBitmap(result.bitmap)
        } else {
          work.bitmapMeta = Object.freeze({
            width: result.width, height: result.height,
            offsetX: result.offsetX, offsetY: result.offsetY,
          })
        }
      }

      const candidate = createContentSurface(gl, size.backingWidth, size.backingHeight)
      transaction.candidate = candidate
      gl.bindFramebuffer(gl.FRAMEBUFFER, candidate.framebuffer)
      gl.viewport(0, 0, Math.max(1, size.backingWidth), Math.max(1, size.backingHeight))
      const background = ownedFrame.config.background
      gl.clearColor(background[0] / 255, background[1] / 255, background[2] / 255, 1)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      assertGlOkay(gl, 'frame clear and blend setup')

      this.#drawSolid(gl, gpu, instances.beforeGlyphs, size.width, size.height)
      if (!await this.#drawGlyphWork(
        transaction, glyphWork, instances.glyphs, ownedFrame, size)) return false
      if (!this.#drawImages(transaction, ownedFrame.images ?? EMPTY_IMAGE_DRAWS, size)) return false
      this.#drawSolid(gl, gpu, instances.decorations, size.width, size.height)
      if (!this.#renderIsCurrent(transaction)) return false

      // Overlay redraws are allowed while glyph rasterization is pending.
      // Merge the newest overlay state immediately before the atomic surface
      // commit so a completed full render cannot resurrect an older blink or
      // selection state captured at render acceptance.
      const committedGeometry = replaceGeometryOverlays(
        ownedFrame.geometry, this.#overlayGeometry ?? ownedFrame.geometry)
      const committedFrame = Object.freeze({ ...ownedFrame, geometry: committedGeometry })
      this.#commitVisible(gl, candidate, collectOverlays(committedGeometry), size)
      const committedAtlas = required(transaction.atlas, 'glyph atlas')
      const oldSurface = this.#surface
      const oldAtlas = this.#atlas === committedAtlas ? null : this.#atlas
      this.#surface = candidate
      transaction.candidate = null
      this.#atlas = committedAtlas
      transaction.atlas = null
      this.#transaction = null
      this.#logicalWidth = size.width
      this.#logicalHeight = size.height
      this.#dpr = ownedFrame.dpr
      this.#presentedFrame = committedFrame
      this.#latestFrame = committedFrame
      this.#overlayGeometry = committedGeometry
      if (oldSurface !== null) deleteContentSurface(gl, oldSurface)
      if (oldAtlas !== null) bestEffort(() => { oldAtlas.dispose() })
      return true
    } catch (error) {
      const failure = classifyGlFailure(gl, error, 'frame rendering')
      if (failure instanceof WebGLContextLostError) {
        if (this.#state === 'ready') {
          ++this.#renderEpoch
          this.#disposeLogicalGpu()
          this.#state = 'lost'
        }
        return false
      }
      if (failure instanceof WebGLGpuError) {
        this.#requestFallback(failure)
        return false
      }
      throw failure
    } finally {
      this.#releaseTransaction(transaction)
    }
  }

  redrawOverlays(geometry: GridGeometry): boolean {
    if (this.#state === 'disposed' || this.#state === 'fallback') return false
    const base = this.#overlayGeometry ?? this.#latestFrame?.geometry ??
      this.#presentedFrame?.geometry ?? EMPTY_GEOMETRY
    const overlayGeometry = retainOverlayGeometry(base, geometry)
    const overlays = collectOverlays(overlayGeometry)
    this.#overlayGeometry = overlayGeometry
    if (this.#latestFrame !== null) {
      const latestGeometry = replaceGeometryOverlays(this.#latestFrame.geometry, overlayGeometry)
      this.#latestFrame = Object.freeze({
        ...this.#latestFrame,
        geometry: latestGeometry,
      })
      this.#overlayGeometry = latestGeometry
    }
    const previous = this.#presentedFrame
    if (previous !== null) {
      const presentedGeometry = replaceGeometryOverlays(previous.geometry, overlayGeometry)
      this.#presentedFrame = Object.freeze({ ...previous, geometry: presentedGeometry })
    }
    const gl = this.#gl
    const gpu = this.#gpu
    if (this.#state !== 'ready' || previous === null || this.#surface === null ||
        gl === null || gpu === null) return false
    try {
      this.#present(gl, gpu, this.#surface, overlays, frameSize(previous))
      return true
    } catch (error) {
      const failure = classifyGlFailure(gl, error, 'overlay redraw')
      if (failure instanceof WebGLContextLostError) {
        ++this.#renderEpoch
        this.#disposeLogicalGpu()
        this.#state = 'lost'
        return false
      }
      if (failure instanceof WebGLGpuError) {
        this.#requestFallback(failure)
        return false
      }
      throw failure
    }
  }

  dispose(): void {
    if (this.#state === 'disposed') return
    ++this.#renderEpoch
    this.#canvas.removeEventListener('webglcontextlost', this.#handleContextLost)
    this.#canvas.removeEventListener('webglcontextrestored', this.#handleContextRestored)
    this.#disposeLogicalGpu()
    this.#latestFrame = null
    this.#presentedFrame = null
    this.#overlayGeometry = null
    this.#activeRender = null
    this.#logicalWidth = 0
    this.#logicalHeight = 0
    this.#dpr = 0
    this.#state = 'disposed'
  }

  #initialize(): boolean {
    const gl = this.#canvas.getContext('webgl2', this.#options.contextAttributes ?? DEFAULT_CONTEXT_ATTRIBUTES)
    if (gl === null) return false
    const gpu = createGpuResources(gl)
    this.#imageContextGeneration++
    this.#imageOwner = `webgl-${this.#imageRendererId}-context-${this.#imageContextGeneration}`
    this.#gl = gl
    this.#gpu = gpu
    this.#atlas = new GlyphAtlas(0, 1, this.#rasterizer, new WebGLAtlasStorage(gl))
    this.#state = 'ready'
    return true
  }

  async #restore(): Promise<void> {
    ++this.#renderEpoch
    this.#state = 'restoring'
    try {
      if (!this.#initialize()) throw new Error('WebGL2 renderer is unavailable after restoration')
      const retained = this.#latestFrame
      if (retained !== null) {
        const redraw = this.render(retained)
        const rendered = await redraw
        if (!rendered) {
          if (this.#isState('ready') && this.#surface === null &&
              (retained.images?.length ?? 0) !== 0 && this.#activeRender === null) {
            // Context loss can evict the bitmap behind an otherwise valid
            // semantic image draw. The store has started re-decoding from its
            // retained PNG; ask GridView for a fresh suppression/draw snapshot
            // instead of falling back or committing a blank suppressed frame.
            this.#notifyRestored()
            return
          }
          // A newer model render may supersede this retained redraw while the
          // restored context remains healthy. Follow the newest render chain
          // until one commits, rejects, or another context loss cancels it.
          while (this.#isState('ready') && this.#surface === null) {
            const superseder = this.#activeRender
            if (superseder === null || superseder === redraw) {
              throw new Error('restored WebGL context has no committed content surface')
            }
            await superseder
          }
          if (this.#isState('ready') && this.#surface !== null) {
            this.#notifyRestored()
            return
          }
          return
        }
      }
      if (!this.#isState('ready')) return
      this.#notifyRestored()
    } catch (error) {
      if (this.#isState('disposed') || this.#isState('lost')) return
      if (error instanceof WebGLContextLostError) {
        ++this.#renderEpoch
        this.#disposeLogicalGpu()
        this.#state = 'lost'
        return
      }
      this.#requestFallback(error)
    }
  }

  #requestFallback(error: unknown): void {
    if (this.#state === 'disposed' || this.#state === 'fallback') return
    ++this.#renderEpoch
    this.#disposeLogicalGpu()
    this.#state = 'fallback'
    if (this.#fallbackNotificationQueued || this.#fallbackNotified) return
    this.#fallbackNotificationQueued = true
    queueMicrotask(() => {
      this.#fallbackNotificationQueued = false
      if (this.#state !== 'fallback' || this.#fallbackNotified) return
      this.#fallbackNotified = true
      this.#invokeCallback(this.#options.onFallbackRequested, error)
    })
  }

  #notifyRestored(): void {
    this.#invokeCallback(this.#options.onRestored)
  }

  #invokeCallback(callback: ((value?: never) => void) | ((error: unknown) => void) | undefined,
                  value?: unknown): void {
    if (callback === undefined) return
    try {
      const result = value === undefined
        ? (callback as () => unknown)()
        : (callback as (error: unknown) => unknown)(value)
      if (result !== undefined && result !== null &&
          (typeof result === 'object' || typeof result === 'function') &&
          'then' in result) {
        void Promise.resolve(result).catch(() => {})
      }
    } catch {}
  }

  #disposeLogicalGpu(): void {
    this.#cancelTransaction()
    const gl = this.#gl
    const atlas = this.#atlas
    const surface = this.#surface
    const gpu = this.#gpu
    this.#discardImageTextures()
    this.#atlas = null
    this.#surface = null
    this.#gpu = null
    this.#gl = null
    if (atlas !== null) bestEffort(() => { atlas.dispose() })
    if (gl !== null && surface !== null) deleteContentSurface(gl, surface)
    if (gl !== null && gpu !== null) deleteGpuResources(gl, gpu)
  }

  #cancelTransaction(): void {
    const transaction = this.#transaction
    if (transaction === null) return
    this.#transaction = null
    transaction.canceled = true
    if (transaction.candidate !== null) {
      deleteContentSurface(transaction.gl, transaction.candidate)
      transaction.candidate = null
    }
    this.#releaseStreamingTexture(transaction)
    this.#releaseTransactionGlyphAlpha(transaction)
    if (transaction.atlas !== null) {
      if (this.#atlas === transaction.atlas) this.#atlas = null
      bestEffort(() => { transaction.atlas?.dispose() })
      transaction.atlas = null
    }
  }

  #releaseTransaction(transaction: RenderTransaction): void {
    if (this.#transaction === transaction) this.#transaction = null
    if (transaction.candidate !== null) {
      deleteContentSurface(transaction.gl, transaction.candidate)
      transaction.candidate = null
    }
    this.#releaseStreamingTexture(transaction)
    this.#releaseTransactionGlyphAlpha(transaction)
    if (transaction.atlas !== null && this.#atlas !== transaction.atlas) {
      bestEffort(() => { transaction.atlas?.dispose() })
      transaction.atlas = null
    }
  }

  #discardImageTextures(): void {
    const store = this.#options.imageStore
    const entries = [...this.#imageTextures.values()]
    this.#imageTextures.clear()
    for (const entry of entries) {
      if (store?.discardRepresentation(entry.contentHash, entry.owner) !== true) {
        const gl = this.#gl
        if (gl !== null) bestEffort(() => { gl.deleteTexture(entry.texture) })
      }
    }
  }

  #releaseStreamingTexture(transaction: RenderTransaction): void {
    const texture = transaction.streamingTexture
    transaction.streamingTexture = null
    if (texture !== null) bestEffort(() => { transaction.gl.deleteTexture(texture) })
  }

  #retainGlyphAlpha(bytes: number): void {
    if (bytes === 0) return
    const nextBytes = this.#glyphAlphaCurrentBytes + bytes
    const nextCount = this.#glyphAlphaCurrentCount + 1
    if (nextBytes > GLYPH_ALPHA_MAX_BYTES || nextCount > GLYPH_ALPHA_MAX_COUNT) {
      throw new RangeError('WebGL glyph alpha working set exceeds its bounded budget')
    }
    this.#glyphAlphaCurrentBytes = nextBytes
    this.#glyphAlphaCurrentCount = nextCount
    this.#glyphAlphaPeakBytes = Math.max(this.#glyphAlphaPeakBytes, nextBytes)
    this.#glyphAlphaPeakCount = Math.max(this.#glyphAlphaPeakCount, nextCount)
  }

  #releaseGlyphAlpha(bytes: number): void {
    if (bytes === 0) return
    this.#glyphAlphaCurrentBytes -= bytes
    this.#glyphAlphaCurrentCount--
  }

  #inspectGlyphBitmap(bitmap: GlyphBitmap): GlyphBitmapMeta {
    validateBitmap(bitmap)
    this.#retainGlyphAlpha(bitmap.alpha.byteLength)
    try {
      return glyphBitmapMeta(bitmap)
    } finally {
      this.#releaseGlyphAlpha(bitmap.alpha.byteLength)
    }
  }

  #releaseStreamingPage(transaction: RenderTransaction): void {
    const page = transaction.streamingPage
    transaction.streamingPage = null
    if (page !== null) this.#releaseGlyphAlpha(page.byteLength)
  }

  #clearBitmapCache(transaction: RenderTransaction): void {
    for (const bitmap of transaction.bitmapCache.values()) {
      this.#releaseGlyphAlpha(bitmap.alpha.byteLength)
    }
    transaction.bitmapCache.clear()
    transaction.bitmapCacheBytes = 0
  }

  #releaseTransactionGlyphAlpha(transaction: RenderTransaction): void {
    this.#releaseStreamingPage(transaction)
    this.#clearBitmapCache(transaction)
  }

  #evictBitmapCache(transaction: RenderTransaction, incomingBytes: number,
                    scratchBytes: number): void {
    const scratchCount = scratchBytes === 0 ? 0 : 1
    while (transaction.bitmapCacheBytes + incomingBytes + scratchBytes > GLYPH_ALPHA_MAX_BYTES ||
           transaction.bitmapCache.size + 1 + scratchCount > GLYPH_ALPHA_MAX_COUNT) {
      const oldest = transaction.bitmapCache.entries().next().value as
        [string, GlyphBitmap] | undefined
      if (oldest === undefined) break
      transaction.bitmapCache.delete(oldest[0])
      transaction.bitmapCacheBytes -= oldest[1].alpha.byteLength
      this.#releaseGlyphAlpha(oldest[1].alpha.byteLength)
    }
    if (transaction.bitmapCacheBytes + incomingBytes + scratchBytes > GLYPH_ALPHA_MAX_BYTES ||
        transaction.bitmapCache.size + 1 + scratchCount > GLYPH_ALPHA_MAX_COUNT) {
      throw new RangeError('WebGL glyph alpha raster cannot fit its bounded working set')
    }
  }

  #renderIsCurrent(transaction: RenderTransaction): boolean {
    return !transaction.canceled && this.#state === 'ready' &&
      this.#gl === transaction.gl && this.#gpu === transaction.gpu &&
      this.#transaction === transaction && this.#renderEpoch === transaction.epoch
  }

  #isState(state: WebGLRendererState): boolean {
    return this.#state === state
  }

  #drawSolid(gl: WebGL2RenderingContext, gpu: GpuResources,
             values: readonly number[], width: number, height: number): void {
    gl.useProgram(gpu.solid.value)
    gl.uniform2f(gpu.solid.resolution, Math.max(1, width), Math.max(1, height))
    gl.bindVertexArray(gpu.solidVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, gpu.solidInstanceBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(values), gl.DYNAMIC_DRAW)
    assertGlOkay(gl, 'solid instance upload')
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, values.length / SOLID_INSTANCE_FLOATS)
    assertGlOkay(gl, 'solid instance draw')
  }

  #drawImages(transaction: RenderTransaction, draws: readonly ImageDraw[],
              size: FrameSize): boolean {
    if (draws.length === 0) return true
    const store = this.#options.imageStore
    if (store === undefined) {
      throw new WebGLGpuError('WebGL image store is unavailable')
    }
    const firstDraws = new Map<string, ImageDraw>()
    const lastUses = new Map<string, number>()
    for (let index = 0; index < draws.length; index++) {
      const draw = draws[index]
      if (!firstDraws.has(draw.contentHash)) firstDraws.set(draw.contentHash, draw)
      lastUses.set(draw.contentHash, index)
    }
    const sources = new Map<string, BrowserImageLease<BrowserImageRenderAsset>>()
    const textures = new Map<string, AcquiredImageTexture>()
    let batchTexture: WebGLTexture | null = null
    let batchFilter: 'nearest' | 'linear' = 'nearest'
    let batchHash = ''
    let values: number[] = []
    const flush = (lastDrawIndex: number): void => {
      if (batchTexture === null || values.length === 0) return
      const flushedHash = batchHash
      try {
        this.#drawImageInstances(transaction.gl, transaction.gpu, values,
          batchTexture, batchFilter, size)
      } finally {
        batchTexture = null
        batchHash = ''
        values = []
        if (lastUses.get(flushedHash) === lastDrawIndex) {
          textures.get(flushedHash)?.lease.release()
          textures.delete(flushedHash)
        }
      }
    }
    try {
      // Pin every resource the ordered frame will still need. When a missing
      // texture replaces its decoded bitmap in the shared LRU, future sources
      // and nonconsecutive A-B-A textures therefore cannot be selected as the
      // eviction victim before their last draw.
      for (const [contentHash, draw] of firstDraws) {
        const existing = this.#imageTextures.get(contentHash)
        if (draw.source === undefined &&
            (existing === undefined || draw.rendererRepresentation !== existing ||
             existing.owner !== this.#imageOwner)) return false
        if (existing !== undefined) {
          const lease = store.acquireRepresentation(
            draw.assetId, existing.owner, existing.bytes, () => {},
          )
          if (lease === null) return false
          textures.set(contentHash, { entry: existing, lease })
          continue
        }
        if (store.acquireForRender === undefined) continue
        const source = store.acquireForRender(draw.assetId)
        if (source === null) return false
        if (source.value.assetId !== draw.assetId ||
            source.value.contentHash !== draw.contentHash ||
            source.value.width !== draw.sourceWidthPixels ||
            source.value.height !== draw.sourceHeightPixels) {
          source.release()
          throw new RangeError('WebGL ready image changed during frame preparation')
        }
        sources.set(contentHash, source)
      }

      for (let index = 0; index < draws.length; index++) {
        const draw = draws[index]
        if (!this.#renderIsCurrent(transaction)) return false
        if (batchTexture !== null &&
            (batchHash !== draw.contentHash || batchFilter !== draw.filter)) flush(index - 1)
        if (batchTexture === null) {
          let acquired = textures.get(draw.contentHash)
          if (acquired === undefined) {
            const source = sources.get(draw.contentHash)
            sources.delete(draw.contentHash)
            const uploaded = this.#uploadImageTexture(transaction, draw, source)
            if (uploaded === null) return false
            acquired = uploaded
            textures.set(draw.contentHash, uploaded)
          }
          batchTexture = acquired.entry.texture
          batchHash = acquired.entry.contentHash
          batchFilter = draw.filter
        }
        values.push(
          draw.destinationX, draw.destinationY,
          draw.destinationWidth, draw.destinationHeight,
          draw.u0, draw.v0, draw.u1, draw.v1,
        )
      }
      flush(draws.length - 1)
      return true
    } finally {
      for (const source of sources.values()) source.release()
      for (const texture of textures.values()) texture.lease.release()
    }
  }

  #uploadImageTexture(transaction: RenderTransaction, draw: ImageDraw,
                      renderLease?: BrowserImageLease<BrowserImageRenderAsset>): AcquiredImageTexture | null {
    const store = this.#options.imageStore
    if (store === undefined) return null
    const source = renderLease?.value.bitmap ?? draw.source
    if (source === undefined) {
      renderLease?.release()
      return null
    }
    if (renderLease !== undefined &&
        (renderLease.value.assetId !== draw.assetId ||
         renderLease.value.contentHash !== draw.contentHash ||
         renderLease.value.width !== draw.sourceWidthPixels ||
         renderLease.value.height !== draw.sourceHeightPixels)) {
      renderLease.release()
      throw new RangeError('WebGL ready image changed during frame preparation')
    }
    const bytes = draw.sourceWidthPixels * draw.sourceHeightPixels * 4
    if (!Number.isSafeInteger(bytes) || bytes <= 0) {
      renderLease?.release()
      throw new RangeError('WebGL image texture byte size is invalid')
    }
    const gl = transaction.gl
    let texture: WebGLTexture | null = null
    try {
      texture = required(gl.createTexture(), 'image texture')
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)
      assertGlOkay(gl, 'image texture upload')
    } catch (error) {
      if (texture !== null) bestEffort(() => { gl.deleteTexture(texture!) })
      throw error
    } finally {
      renderLease?.release()
    }
    const uploadedTexture = required(texture, 'uploaded image texture')
    let entry: ImageTextureEntry | undefined
    const releaseRepresentation = (): void => {
      if (entry !== undefined && this.#imageTextures.get(entry.contentHash) === entry) {
        this.#imageTextures.delete(entry.contentHash)
      }
      bestEffort(() => { gl.deleteTexture(uploadedTexture) })
    }
    let representation: BrowserImageLease<undefined> | null
    try {
      // WebGL has synchronously copied the source. Relinquish the decoded
      // bitmap before charging the RGBA texture so the two representations
      // transfer one shared budget slot even at the per-image size limit.
      representation = store.acquireRepresentation(
        draw.assetId, this.#imageOwner, bytes, releaseRepresentation,
      )
    } catch (error) {
      bestEffort(() => { gl.deleteTexture(uploadedTexture) })
      throw error
    }
    if (representation === null) {
      bestEffort(() => { gl.deleteTexture(uploadedTexture) })
      return null
    }
    entry = Object.freeze({
      contentHash: draw.contentHash,
      owner: this.#imageOwner,
      texture: uploadedTexture,
      bytes,
    })
    this.#imageTextures.set(draw.contentHash, entry)
    return { entry, lease: representation }
  }

  #drawImageInstances(gl: WebGL2RenderingContext, gpu: GpuResources,
                      values: readonly number[], texture: WebGLTexture,
                      filter: 'nearest' | 'linear', size: FrameSize): void {
    gl.useProgram(gpu.image.value)
    gl.uniform2f(gpu.image.resolution, Math.max(1, size.width), Math.max(1, size.height))
    gl.uniform1i(gpu.image.page, 0)
    gl.bindVertexArray(gpu.imageVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, gpu.imageInstanceBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(values), gl.DYNAMIC_DRAW)
    assertGlOkay(gl, 'image instance upload')
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, texture)
    const sampling = filter === 'nearest' ? gl.NEAREST : gl.LINEAR
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, sampling)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, sampling)
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, values.length / IMAGE_INSTANCE_FLOATS)
    assertGlOkay(gl, 'image instance draw')
  }

  async #drawGlyphWork(transaction: RenderTransaction, workItems: readonly GlyphWork[],
                       orderedGlyphs: readonly GeometryGlyph[], frame: WebGLRenderFrame,
                       size: FrameSize): Promise<boolean> {
    if (!this.#renderIsCurrent(transaction) || transaction.atlas === null) return false
    const atlas = transaction.atlas
    const liveGlyphs = new Map<string, AtlasGlyph<WebGLAtlasPage>>()
    const remaining: GlyphWork[] = []
    for (const work of workItems) {
      const glyph = atlas.lookup(work.key)
      if (glyph === null || !atlas.isCurrent(glyph)) {
        remaining.push(work)
        continue
      }
      if (glyph.width === 0 || glyph.height === 0) continue
      liveGlyphs.set(glyphKeyId(work.key), glyph)
    }
    const batches = new Map<number, { readonly texture: WebGLTexture; readonly values: number[] }>()
    for (const command of orderedGlyphs) {
      const glyph = liveGlyphs.get(glyphKeyId(glyphKey(command)))
      if (glyph === undefined) continue
      let batch = batches.get(glyph.pageIndex)
      if (batch === undefined) {
        batch = { texture: glyph.page.texture, values: [] }
        batches.set(glyph.pageIndex, batch)
      }
      this.#appendGlyphInstance(batch.values, command, glyph.offsetX, glyph.offsetY,
        glyph.width, glyph.height, glyph.dpr, frame.config,
        glyph.x / ATLAS_PAGE_SIZE, glyph.y / ATLAS_PAGE_SIZE,
        (glyph.x + glyph.width) / ATLAS_PAGE_SIZE,
        (glyph.y + glyph.height) / ATLAS_PAGE_SIZE)
    }
    const orderedBatches = [...batches.entries()].sort(([left], [right]) => left - right)
    for (const [, batch] of orderedBatches) {
      if (!this.#renderIsCurrent(transaction)) return false
      this.#drawGlyphInstances(
        transaction.gl, transaction.gpu, batch.values, batch.texture, size)
    }
    if (remaining.length !== 0) {
      // The committed content surface already owns every atlas-page draw.
      // Release those R8 pages before allocating the single streaming/tiled
      // texture so transient overflow stays inside the global 4 MiB budget.
      if (this.#atlas === atlas) this.#atlas = null
      bestEffort(() => { atlas.dispose() })
      transaction.atlas = new GlyphAtlas(
        frame.config.configGeneration, frame.dpr,
        this.#rasterizer, new WebGLAtlasStorage(transaction.gl))
    }
    return await this.#drawOverflowGlyphs(
      transaction, remaining, orderedGlyphs, frame, size)
  }

  async #drawOverflowGlyphs(transaction: RenderTransaction, workItems: readonly GlyphWork[],
                            orderedGlyphs: readonly GeometryGlyph[], frame: WebGLRenderFrame,
                            size: FrameSize): Promise<boolean> {
    if (workItems.length === 0) return true
    const gl = transaction.gl
    const remaining = new Map(workItems.map((work) => [glyphKeyId(work.key), work]))
    const placements = new Map<string, StreamingGlyphPlacement>()
    let pageCommands: GeometryGlyph[] = []
    let shelfX = 0
    let shelfY = 0
    let shelfHeight = 0
    const reset = (): void => {
      transaction.streamingPage?.fill(0)
      placements.clear()
      pageCommands = []
      shelfX = 0
      shelfY = 0
      shelfHeight = 0
    }
    const flush = (): void => {
      if (pageCommands.length === 0) return
      const page = transaction.streamingPage
      if (page === null) throw new Error('WebGL streaming glyph page is unavailable')
      if (transaction.streamingTexture === null) {
        transaction.streamingTexture = required(gl.createTexture(), 'streaming glyph texture')
      }
      const texture = transaction.streamingTexture
      this.#configureGlyphTexture(gl, texture)
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, ATLAS_PAGE_SIZE, ATLAS_PAGE_SIZE,
        0, gl.RED, gl.UNSIGNED_BYTE, page)
      assertGlOkay(gl, 'streaming glyph page upload')
      const values: number[] = []
      for (const command of pageCommands) {
        const placement = placements.get(glyphKeyId(glyphKey(command)))
        if (placement === undefined) throw new Error('WebGL streaming glyph placement is missing')
        this.#appendGlyphInstance(values, command,
          placement.offsetX, placement.offsetY, placement.width, placement.height,
          placement.dpr, frame.config,
          placement.u0, placement.v0, placement.u1, placement.v1)
      }
      this.#drawGlyphInstances(gl, transaction.gpu, values, texture, size)
      reset()
    }
    try {
      for (let index = 0; index < orderedGlyphs.length; index++) {
        const command = orderedGlyphs[index]
        const id = glyphKeyId(glyphKey(command))
        const work = remaining.get(id)
        if (work === undefined) continue
        const meta = work.bitmapMeta
        if (meta === undefined) throw new Error('WebGL overflow glyph metadata is missing')
        if (meta.width === 0 || meta.height === 0) continue
        if (meta.width > ATLAS_PAGE_SIZE || meta.height > ATLAS_PAGE_SIZE) {
          flush()
          this.#releaseStreamingPage(transaction)
          const tileBytes = Math.min(meta.width, ATLAS_PAGE_SIZE) *
            Math.min(meta.height, ATLAS_PAGE_SIZE)
          const bitmap = await this.#overflowBitmap(
            transaction, work, frame, tileBytes)
          if (bitmap === null) return false
          const commands = [command]
          while (index + 1 < orderedGlyphs.length &&
                 glyphKeyId(glyphKey(orderedGlyphs[index + 1])) === id) {
            commands.push(orderedGlyphs[++index])
          }
          this.#drawTiledGlyphBatch(gl, transaction.gpu, commands,
            { ...work.key, configGeneration: frame.config.configGeneration, dpr: frame.dpr },
            bitmap, frame.config, size)
          continue
        }
        if (!placements.has(id)) {
          const bitmap = await this.#overflowBitmap(
            transaction, work, frame, ATLAS_PAGE_SIZE * ATLAS_PAGE_SIZE)
          if (bitmap === null) return false
          if (shelfX + bitmap.width > ATLAS_PAGE_SIZE) {
            shelfY += shelfHeight
            shelfX = 0
            shelfHeight = 0
          }
          if (shelfY + bitmap.height > ATLAS_PAGE_SIZE) flush()
          if (transaction.streamingPage === null) {
            const page = new Uint8Array(ATLAS_PAGE_SIZE * ATLAS_PAGE_SIZE)
            this.#retainGlyphAlpha(page.byteLength)
            transaction.streamingPage = page
          }
          const page = transaction.streamingPage
          const x = shelfX
          const y = shelfY
          for (let row = 0; row < bitmap.height; row++) {
            page.set(bitmap.alpha.subarray(row * bitmap.width, (row + 1) * bitmap.width),
              (y + row) * ATLAS_PAGE_SIZE + x)
          }
          shelfX += bitmap.width
          shelfHeight = Math.max(shelfHeight, bitmap.height)
          placements.set(id, Object.freeze({
            ...meta, dpr: frame.dpr,
            u0: x / ATLAS_PAGE_SIZE, v0: y / ATLAS_PAGE_SIZE,
            u1: (x + bitmap.width) / ATLAS_PAGE_SIZE,
            v1: (y + bitmap.height) / ATLAS_PAGE_SIZE,
          }))
        }
        pageCommands.push(command)
      }
      if (!this.#renderIsCurrent(transaction)) return false
      flush()
      return true
    } finally {
      this.#releaseStreamingTexture(transaction)
      this.#releaseTransactionGlyphAlpha(transaction)
    }
  }

  async #overflowBitmap(transaction: RenderTransaction, work: GlyphWork,
                        frame: WebGLRenderFrame, scratchBytes: number): Promise<GlyphBitmap | null> {
    const id = glyphKeyId(work.key)
    const cached = transaction.bitmapCache.get(id)
    if (cached !== undefined) {
      transaction.bitmapCache.delete(id)
      const scratchCount = scratchBytes === 0 ? 0 : 1
      while (transaction.bitmapCacheBytes + scratchBytes > GLYPH_ALPHA_MAX_BYTES ||
             transaction.bitmapCache.size + 1 + scratchCount > GLYPH_ALPHA_MAX_COUNT) {
        const oldest = transaction.bitmapCache.entries().next().value as
          [string, GlyphBitmap] | undefined
        if (oldest === undefined) break
        transaction.bitmapCache.delete(oldest[0])
        transaction.bitmapCacheBytes -= oldest[1].alpha.byteLength
        this.#releaseGlyphAlpha(oldest[1].alpha.byteLength)
      }
      transaction.bitmapCache.set(id, cached)
      return cached
    }
    const meta = work.bitmapMeta
    if (meta === undefined) throw new Error('WebGL overflow glyph metadata is missing')
    const bytes = meta.width * meta.height
    this.#evictBitmapCache(transaction, bytes, scratchBytes)
    const request = Object.freeze({
      ...work.key, configGeneration: frame.config.configGeneration, dpr: frame.dpr,
    })
    const bitmap = await this.#rasterizer.rasterize(request)
    if (!this.#renderIsCurrent(transaction)) return null
    validateBitmap(bitmap)
    if (!bitmapMatchesMeta(bitmap, meta)) {
      throw new RangeError('WebGL glyph rasterizer changed bitmap geometry between passes')
    }
    this.#retainGlyphAlpha(bitmap.alpha.byteLength)
    transaction.bitmapCache.set(id, bitmap)
    transaction.bitmapCacheBytes += bitmap.alpha.byteLength
    return bitmap
  }

  #configureGlyphTexture(gl: WebGL2RenderingContext, texture: WebGLTexture): void {
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  }

  #drawTiledGlyphBatch(gl: WebGL2RenderingContext, gpu: GpuResources,
                       commands: readonly GeometryGlyph[], request: GlyphRasterRequest,
                       bitmap: GlyphBitmap, config: GridRendererConfig, size: FrameSize): void {
    const texture = required(gl.createTexture(), 'direct glyph texture')
    try {
      this.#configureGlyphTexture(gl, texture)
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      for (const command of commands) {
        for (let tileY = 0; tileY < bitmap.height; tileY += ATLAS_PAGE_SIZE) {
          const tileHeight = Math.min(ATLAS_PAGE_SIZE, bitmap.height - tileY)
          for (let tileX = 0; tileX < bitmap.width; tileX += ATLAS_PAGE_SIZE) {
            const tileWidth = Math.min(ATLAS_PAGE_SIZE, bitmap.width - tileX)
            const alpha = new Uint8Array(tileWidth * tileHeight)
            for (let row = 0; row < tileHeight; row++) {
              const source = (tileY + row) * bitmap.width + tileX
              alpha.set(bitmap.alpha.subarray(source, source + tileWidth), row * tileWidth)
            }
            this.#retainGlyphAlpha(alpha.byteLength)
            try {
              gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, tileWidth, tileHeight,
                0, gl.RED, gl.UNSIGNED_BYTE, alpha)
              assertGlOkay(gl, 'tiled direct glyph upload')
              const values: number[] = []
              this.#appendGlyphInstance(values, command,
                bitmap.offsetX + tileX / request.dpr,
                bitmap.offsetY + tileY / request.dpr,
                tileWidth, tileHeight, request.dpr, config, 0, 0, 1, 1)
              this.#drawGlyphInstances(gl, gpu, values, texture, size)
            } finally {
              this.#releaseGlyphAlpha(alpha.byteLength)
            }
          }
        }
      }
    } finally {
      gl.deleteTexture(texture)
    }
  }

  #appendGlyphInstance(target: number[], command: GeometryGlyph,
                       offsetX: number, offsetY: number, width: number, height: number,
                       dpr: number, config: GridRendererConfig,
                       u0: number, v0: number, u1: number, v1: number): void {
    target.push(
      command.x + offsetX,
      command.y + config.baseline + offsetY,
      width / dpr,
      height / dpr,
      command.color[0] / 255,
      command.color[1] / 255,
      command.color[2] / 255,
      command.alpha / 255,
      u0, v0, u1, v1,
    )
  }

  #drawGlyphInstances(gl: WebGL2RenderingContext, gpu: GpuResources,
                      values: readonly number[], texture: WebGLTexture,
                      size: FrameSize): void {
    gl.useProgram(gpu.glyph.value)
    gl.uniform2f(gpu.glyph.resolution, Math.max(1, size.width), Math.max(1, size.height))
    gl.uniform1i(gpu.glyph.page, 0)
    gl.bindVertexArray(gpu.glyphVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, gpu.glyphInstanceBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(values), gl.DYNAMIC_DRAW)
    assertGlOkay(gl, 'glyph instance upload')
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, values.length / GLYPH_INSTANCE_FLOATS)
    assertGlOkay(gl, 'glyph instance draw')
  }

  #commitVisible(gl: WebGL2RenderingContext, candidate: ContentSurface,
                 overlays: readonly number[], size: FrameSize): void {
    const oldWidth = this.#canvas.width
    const oldHeight = this.#canvas.height
    const oldStyleWidth = this.#canvas.style.width
    const oldStyleHeight = this.#canvas.style.height
    const backingChanged = oldWidth !== size.backingWidth || oldHeight !== size.backingHeight
    try {
      if (backingChanged) setCanvasSizeExact(this.#canvas, size.backingWidth, size.backingHeight)
      if (this.#canvas.style.width !== `${size.width}px`) this.#canvas.style.width = `${size.width}px`
      if (this.#canvas.style.height !== `${size.height}px`) this.#canvas.style.height = `${size.height}px`
      this.#present(gl, required(this.#gpu, 'resource set'), candidate, overlays, size)
    } catch (error) {
      if (backingChanged) bestEffort(() => { setCanvasSizeExact(this.#canvas, oldWidth, oldHeight) })
      bestEffort(() => { this.#canvas.style.width = oldStyleWidth })
      bestEffort(() => { this.#canvas.style.height = oldStyleHeight })
      const previous = this.#surface
      if (previous !== null && this.#gpu !== null &&
          this.#canvas.width === oldWidth && this.#canvas.height === oldHeight) {
        bestEffort(() => {
          this.#present(gl, this.#gpu!, previous,
            this.#presentedFrame === null ? [] : collectOverlays(this.#presentedFrame.geometry),
            { width: this.#logicalWidth, height: this.#logicalHeight,
              backingWidth: oldWidth, backingHeight: oldHeight })
        })
      }
      throw error
    }
  }

  #present(gl: WebGL2RenderingContext, gpu: GpuResources, surface: ContentSurface,
           overlays: readonly number[], size: FrameSize): void {
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, surface.framebuffer)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
    gl.blitFramebuffer(0, 0, surface.width, surface.height,
      0, 0, size.backingWidth, size.backingHeight, gl.COLOR_BUFFER_BIT, gl.NEAREST)
    assertGlOkay(gl, 'content surface blit')
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.viewport(0, 0, Math.max(1, size.backingWidth), Math.max(1, size.backingHeight))
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    this.#drawSolid(gl, gpu, overlays, size.width, size.height)
  }
}

export { WebGLAtlasStorage }
